
import type {
  ArrayConfigFieldSchema,
  ConfigFieldSchema,
  NumberConfigFieldSchema,
  StringConfigFieldSchema,
} from '@empathyco/playboard-core-domain'
import type { Component, PropType } from 'vue/types/options'
import type {
  Dictionary,
  ServiceConfigFieldError,
  ServiceConfigFieldMessages,
  ServiceConfigFieldValidityState,
} from '../../models'

import { ConfigFieldTypeEnum, ConfigFieldVisibilityEnum } from '@empathyco/playboard-core-domain'

import { defineComponent } from 'vue'

import BooleanServiceConfigField from './boolean-service-config-field.component.vue'
import NumberServiceConfigField from './number-service-config-field.component.vue'
import ObjectArrayServiceConfigField from './object-array-service-config-field.component.vue'
import ObjectServiceConfigField from './object-service-config-field.component.vue'
import StringArrayServiceConfigField from './string-array-service-config-field.component.vue'
import StringServiceConfigField from './string-service-config-field.component.vue'

export default defineComponent({
  name: 'ServiceConfigField',
  components: {
    ObjectServiceConfigField,
    ObjectArrayServiceConfigField,
  },
  props: {
    id: {
      type: String,
    },
    value: {
      type: [Object, Array, String, Number, Boolean],
    },
    schema: {
      type: Object as PropType<ConfigFieldSchema>,
      required: true,
    },
    required: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    level: {
      type: Number,
      default: 1,
    },
    validityState: {
      type: Object as PropType<ServiceConfigFieldValidityState>,
      required: true,
    },
    messages: {
      type: Object as PropType<ServiceConfigFieldMessages>,
    },
  },
  data() {
    return {
      ConfigFieldTypeEnum,
    }
  },
  computed: {
    label(): string {
      return this.messages?.label || this.schema.front_metadata?.display_name || this.id
    },
    description(): string {
      return this.messages?.description || this.schema.description
    },
    options() {
      return this.messages?.options || this.schema.front_metadata?.options
    },
    _readonly(): boolean {
      if (this.readonly) {
        return true
      }
      return this.schema.front_metadata?.visibility === ConfigFieldVisibilityEnum.READONLY
    },
    isObjectArray(): boolean {
      return (
        this.schema.type === ConfigFieldTypeEnum.ARRAY &&
        this.schema.items?.type === ConfigFieldTypeEnum.OBJECT
      )
    },
    typeComponent(): Component {
      const typeFn = {
        [ConfigFieldTypeEnum.ARRAY]: () => {
          return {
            [ConfigFieldTypeEnum.STRING]: StringArrayServiceConfigField,
          }[this.schema.items?.type]
        },
        [ConfigFieldTypeEnum.STRING]: () => StringServiceConfigField,
        [ConfigFieldTypeEnum.NUMBER]: () => NumberServiceConfigField,
        [ConfigFieldTypeEnum.INTEGER]: () => NumberServiceConfigField,
        [ConfigFieldTypeEnum.BOOLEAN]: () => BooleanServiceConfigField,
      }[this.schema.type]
      return !!typeFn && typeFn()
    },
    typeComponentProps(): Record<string, any> {
      const typeFn = {
        [ConfigFieldTypeEnum.ARRAY]: () => {
          return {
            [ConfigFieldTypeEnum.STRING]: this.getStringArrayComponentProps(this.schema),
          }[this.schema.items?.type]
        },
        [ConfigFieldTypeEnum.STRING]: () => this.getStringComponentProps(this.schema),
        [ConfigFieldTypeEnum.NUMBER]: () => this.getNumberComponentProps(this.schema),
        [ConfigFieldTypeEnum.INTEGER]: () => this.getNumberComponentProps(this.schema),
      }[this.schema.type]
      return !!typeFn && typeFn()
    },
    errors(): string[] {
      return this.getErrorMessages(this.validityState.errors)
    },
    itemErrors(): Dictionary<string[]> {
      const items = Object.entries(this.validityState.items)
      return items.reduce(
        (
          itemErrors: Dictionary<string[]>,
          [itemId, itemValidityState]: [string, ServiceConfigFieldValidityState],
        ) => {
          const itemErrorMessages = this.getErrorMessages(itemValidityState.errors)
          if (itemErrorMessages.length) {
            itemErrors[itemId] = itemErrorMessages
          }
          return itemErrors
        },
        {},
      )
    },
  },
  methods: {
    getPropertyMessages(id: string): ServiceConfigFieldMessages {
      return this.messages?.fields?.[id]
    },
    getStringArrayComponentProps(schema: ArrayConfigFieldSchema): {
      itemErrors: Dictionary<string[]>
      options?: string[]
      minlength?: number
      maxlength?: number
      rows?: number
    } {
      const items = schema.items as StringConfigFieldSchema
      return {
        itemErrors: this.itemErrors,
        ...(!!items && this.getStringComponentProps(items)),
      }
    },
    getStringComponentProps(schema: StringConfigFieldSchema): {
      options?: string[]
      minlength?: number
      maxlength?: number
      rows?: number
    } {
      return {
        options: schema.front_metadata?.enum ?? schema.enum,
        minlength: schema.minLength,
        maxlength: schema.maxLength,
        rows: schema.front_metadata?.rows,
      }
    },
    getNumberComponentProps(schema: NumberConfigFieldSchema): {
      min?: number
      max?: number
    } {
      let min: number, max: number
      if (!!schema.minimum || schema.minimum === 0) {
        min = schema.minimum
      } else if (!!schema.exclusiveMinimum || schema.exclusiveMinimum === 0) {
        min = schema.exclusiveMinimum
        if (schema.type === ConfigFieldTypeEnum.INTEGER) {
          min += 1
        }
      }
      if (!!schema.maximum || schema.maximum === 0) {
        max = schema.maximum
      } else if (!!schema.exclusiveMaximum || schema.exclusiveMaximum === 0) {
        max = schema.exclusiveMaximum
        if (schema.type === ConfigFieldTypeEnum.INTEGER) {
          min -= 1
        }
      }
      return {
        min,
        max,
      }
    },
    getErrorMessages(errors: ServiceConfigFieldError[]): string[] {
      const errorMessages = this.$epb.messages.instanceManagement.serviceConfig.fieldErrors
      return errors.map((error: ServiceConfigFieldError) => {
        if (!errorMessages.hasOwnProperty(error.id)) {
          return error.id
        }
        return this.$epb.utils.compileMessage(errorMessages[error.id], error.details || {})
      })
    },
  },
})
